import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { applyTheme } from "@/lib/themeApplier";
import {
  LayoutDashboard, CalendarDays, Package, FileText, Users, LogOut, Globe, CalendarOff,
  FileSignature, Images, ExternalLink, Star, Moon, Sun, Menu, X, Sparkles, Building2,
  ChevronRight, Inbox,
} from "lucide-react";
import Logo from "@/components/Logo";

// ── Navigation groups ─────────────────────────────────────────────────────────
const navGroups = [
  {
    label: "Workflow",
    items: [
      { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/admin/bookings", label: "Bookings", icon: CalendarDays },
      { to: "/admin/availability", label: "Availability", icon: CalendarOff },
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/contracts", label: "Contracts", icon: FileSignature },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/services", label: "Services", icon: Package },
      { to: "/admin/galleries", label: "Galleries", icon: Images },
    ],
  },
  {
    label: "Studio",
    items: [
      { to: "/admin/reviews", label: "Reviews", icon: Star },
      { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
      { to: "/admin/customers", label: "Customers", icon: Users },
      { to: "/admin/waitlist", label: "Waitlist", icon: Sparkles },
      { to: "/admin/studio-invites", label: "Studios", icon: Building2 },
      { to: "/admin/site", label: "Site Settings", icon: Globe },
    ],
  },
];

// Flat list kept for the mobile "current section" label lookup
const allItems = navGroups.flatMap((g) => g.items);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("admin_theme") === "dark"; }
    catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("admin_theme", dark ? "dark" : "light"); }
    catch { /* ignore */ }
  }, [dark]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    let cleanup = () => {};
    let cancelled = false;
    async function loadTheme() {
      try {
        const { data } = await api.get("/admin/site");
        if (cancelled) return;
        cleanup();
        cleanup = applyTheme({ themeId: data.theme_id || "editorial", overrides: data.theme_overrides || {} });
      } catch { /* not logged in yet — skip */ }
    }
    loadTheme();
    const onChange = () => loadTheme();
    window.addEventListener("lumetier:theme-changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("lumetier:theme-changed", onChange);
      cleanup();
    };
  }, []);

  const currentItem =
    allItems.find((it) =>
      it.end ? location.pathname === it.to : location.pathname.startsWith(it.to)
    ) || allItems[0];

  // User initials
  const initials = (user?.email || "A")[0].toUpperCase();

  // ── Sidebar markup ─────────────────────────────────────────────────────────
  const Sidebar = (
    <div className="flex flex-col h-full bg-[#0F0E0D] text-stone-300">
      {/* Logo header */}
      <div className="h-20 flex items-center px-6 border-b border-white/[0.07] shrink-0">
        <Link to="/" title="Back to main site" data-testid="admin-logo-home-link">
          <Logo className="h-9 brightness-0 invert opacity-85" testid="admin-sidebar-logo" />
        </Link>
      </div>

      {/* Grouped navigation */}
      <nav className="flex-1 px-3 py-5 overflow-y-auto space-y-6" aria-label="Studio Manager navigation">
        {navGroups.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-2 text-[9.5px] uppercase tracking-[0.28em] text-stone-500 font-medium select-none">
              {group.label}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((it) => {
                const Icon = it.icon;
                return (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      end={it.end}
                      data-testid={`admin-nav-${it.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-150 rounded-[3px] ${
                          isActive
                            ? "bg-white/[0.1] text-stone-100 border-l-2 border-[hsl(var(--gold))] pl-[10px] font-medium"
                            : "text-stone-400 hover:bg-white/[0.06] hover:text-stone-100 border-l-2 border-transparent"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0 opacity-80" />
                      {it.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom utility section */}
      <div className="px-3 pb-4 border-t border-white/[0.07] pt-4 shrink-0 space-y-1">
        {/* View site + dark toggle row */}
        <div className="flex items-center justify-between px-3 py-1.5">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-200 transition-colors uppercase tracking-[0.18em]"
            data-testid="back-to-site-link"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View site
          </Link>
          <button
            onClick={() => setDark((d) => !d)}
            className="inline-flex items-center justify-center w-8 h-8 border border-white/[0.1] hover:border-white/30 text-stone-500 hover:text-stone-200 transition-colors rounded-[3px]"
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="admin-theme-toggle"
          >
            {dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Lumetier link */}
        <Link
          to="/lumetier"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-stone-500 hover:text-stone-200 transition-colors uppercase tracking-[0.18em]"
          data-testid="admin-lumetier-link"
        >
          <Sparkles className="w-3.5 h-3.5" /> Lumetier
        </Link>

        {/* User profile row */}
        <div className="mt-2 flex items-center gap-3 px-3 py-3 border-t border-white/[0.07]">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-sm bg-[hsl(var(--gold))]/15 border border-[hsl(var(--gold))]/30 flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold text-[hsl(var(--gold))]">{initials}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-xs text-stone-400 truncate max-w-[130px]"
              data-testid="admin-current-user"
              title={user?.email}
            >
              {user?.email}
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="shrink-0 text-stone-500 hover:text-stone-200 transition-colors"
            aria-label="Log out"
            data-testid="admin-logout-btn"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`flex h-screen overflow-hidden bg-muted/30 text-foreground ${dark ? "admin-dark" : ""}`}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col" data-testid="admin-sidebar">
        {Sidebar}
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          data-testid="admin-drawer-backdrop"
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-mobile-drawer"
        aria-hidden={!mobileOpen}
      >
        {Sidebar}
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto min-w-0 bg-background">
        {/* Mobile topbar */}
        <div
          className="lg:hidden sticky top-0 z-30 flex items-center gap-3 h-14 px-4 bg-[#0F0E0D] border-b border-white/[0.07]"
          data-testid="admin-mobile-topbar"
        >
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="inline-flex items-center justify-center w-9 h-9 -ml-1 text-stone-300"
            data-testid="admin-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Logo className="h-6 brightness-0 invert opacity-85" />
          <div className="ml-auto flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-400">
            <ChevronRight className="w-3 h-3 opacity-50" />
            <span data-testid="admin-mobile-current-section">{currentItem.label}</span>
          </div>
        </div>

        {/* Mobile drawer close button */}
        {mobileOpen && (
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
            className="lg:hidden fixed top-3 left-[calc(min(72vw,18rem)-2.75rem)] z-[60] inline-flex items-center justify-center w-9 h-9 bg-stone-800 border border-stone-600 text-stone-200"
            data-testid="admin-menu-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Page content */}
        <div className="p-5 sm:p-8 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
